import { Stack } from '@mui/material';
import { ChartsTooltipContainer } from '@mui/x-charts';
import {
  DatasetElementType,
  SeriesValueFormatter,
} from '@mui/x-charts/internals';
import { Stage } from '../../../../../services/entities/impl/blfApi/schemas/stage';
import { useTranslation } from '../../../../../services/translation/hooks/useTranslation';
import { BarChartTooltip } from '../../../components/ReportBarChart/components/BarChartTooltip';
import { EstimatedCountDisplay } from '../../../components/ReportBarChart/components/EstimatedCountDisplay';
import { ReportChartLegend } from '../../../components/ReportBarChart/components/ReportChartLegend';
import { StyledBarChart } from '../../../components/ReportBarChart/components/StyledBarChart';
import { CountMode } from '../../../components/ReportBarChart/types';
import { useCountModeLabels } from '../../hooks/useCountModeLabels';
import { getStageColor } from '../../utils/getStageColor';
import { useStageReportChartData } from './hooks/useStageReportChartData';

type Props = {
  scanDate: string;
  ranchIds: string[];
  tableLoading: boolean;
  stages: Stage[];
  countMode: CountMode;
  onBarClick: (
    index: number,
    data: DatasetElementType<string | number>[]
  ) => void;
};

export const StageReportBarChart = ({
  scanDate,
  stages,
  ranchIds,
  tableLoading,
  countMode,
  onBarClick,
}: Props) => {
  const { data, yAxisValueFormatter } = useStageReportChartData(
    scanDate,
    ranchIds,
    tableLoading,
    countMode
  );
  const { t } = useTranslation();
  const countModeLabel = useCountModeLabels();

  const tooltipFormatter: SeriesValueFormatter<number | null> = (
    value,
    { dataIndex }
  ) => {
    if (value === null) {
      return '';
    }

    const totalCount = data?.at(dataIndex)?.totalCount as number | undefined;

    return totalCount ? `${((value / totalCount) * 100).toFixed(1)}%` : '';
  };

  const series = stages.map((stage) => ({
    id: stage.id,
    dataKey: stage.id,
    label: stage.name,
    stack: '1',
    color: getStageColor(stage),
    valueFormatter: tooltipFormatter,
  }));

  return (
    <Stack spacing={4}>
      {countMode === 'total' && <EstimatedCountDisplay data={data} />}
      <ReportChartLegend items={series} />
      <StyledBarChart
        onItemClick={(_, barItemIdentifier) =>
          onBarClick(barItemIdentifier.dataIndex, data)
        }
        height={data?.length ? data.length * 16 : 100}
        series={series}
        dataset={data}
        yAxis={[
          {
            scaleType: 'band',
            dataKey: 'dataKey',
            valueFormatter: yAxisValueFormatter,
            disableTicks: true,
            categoryGapRatio: 0.5,
            width: 80,
          },
        ]}
        localeText={{
          noData: t('No data matches the selected filters'),
        }}
        slots={{
          tooltip: () => (
            <ChartsTooltipContainer>
              <BarChartTooltip totalLabel={countModeLabel[countMode]} />
            </ChartsTooltipContainer>
          ),
        }}
      />
    </Stack>
  );
};
